import { getDimensionValue } from "./calculations";
import {
  AllocationSplit,
  MarketTarget,
  ReportConfiguration,
  ReportingRecord,
} from "./types";

/**
 * Market revenue/profit targets: combining configured targets against the current
 * filters, and importing/exporting them as the TSV shape a planning spreadsheet already
 * produces (one row per market, a revenue and a profit column per month).
 */

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_INDEX: Record<string, string> = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
};

/** `"2026-01"` -> `"Jan-26"`, matching the column header a planning sheet already uses. */
export function formatMonthColumnLabel(month: string): string {
  const [year, monthNumber] = month.split("-");
  const index = Number(monthNumber) - 1;

  return `${MONTH_ABBREVIATIONS[index] ?? monthNumber}-${year.slice(2)}`;
}

function parseMonthColumnLabel(label: string): string | null {
  const match = label.trim().match(/^([A-Za-z]{3})-(\d{2})$/);
  if (!match) return null;

  const month = MONTH_INDEX[match[1].toLowerCase()];
  if (!month) return null;

  return `${2000 + Number(match[2])}-${month}`;
}

function parseTargetAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;

  const stripped = trimmed.replace(/[^0-9.-]+/g, "");
  if (stripped === "" || stripped === "-") return null;

  const value = Number(stripped);
  return Number.isFinite(value) ? value : null;
}

/**
 * Every market the current dataset has at least one record for, regardless of the active
 * period/filters - a blank template should offer every market worth setting a target for,
 * not just whatever happens to be in view right now.
 */
export function getDistinctMarketValues(
  records: ReportingRecord[],
  configuration: ReportConfiguration,
): string[] {
  return [
    ...new Set(
      records
        .map((record) =>
          getDimensionValue(record, null, configuration, "market"),
        )
        .filter((value) => value !== "Unassigned"),
    ),
  ].sort((a, b) => a.localeCompare(b));
}

/** A blank TSV an admin can fill in a spreadsheet and paste back, one row per market. */
export function buildTargetsTemplate(markets: string[], year: number): string {
  const months = Array.from(
    { length: 12 },
    (_, index) => `${year}-${String(index + 1).padStart(2, "0")}`,
  );
  const header = [
    "Market",
    ...months.flatMap((month) => {
      const label = formatMonthColumnLabel(month);
      return [`${label}_revenue`, `${label}_profit`];
    }),
  ];
  const rows = markets.map((market) => [
    market,
    ...months.flatMap(() => ["", ""]),
  ]);

  return [header, ...rows].map((row) => row.join("\t")).join("\n");
}

/**
 * Parses a pasted TSV of the same shape `buildTargetsTemplate` produces. Unrecognized header
 * columns are skipped rather than failing the whole import, since a planning sheet often
 * carries extra columns (totals, notes) this module has no use for. A cell that cannot be
 * read as a number is reported but does not stop the rest of the paste from importing.
 */
export function parseTargetsTsv(tsv: string): {
  targets: MarketTarget[];
  errors: string[];
} {
  const lines = tsv.split(/\r?\n/).filter((line) => line.trim() !== "");

  if (lines.length < 2) {
    return {
      targets: [],
      errors: ["Paste a header row and at least one market row."],
    };
  }

  const header = lines[0].split("\t");
  const columns: {
    index: number;
    month: string;
    metric: "revenue" | "profit";
  }[] = [];

  header.forEach((cell, index) => {
    if (index === 0) return;

    const match = cell.trim().match(/^([A-Za-z]{3}-\d{2})_(revenue|profit)$/i);
    if (!match) return;

    const month = parseMonthColumnLabel(match[1]);
    if (!month) return;

    columns.push({
      index,
      month,
      metric: match[2].toLowerCase() as "revenue" | "profit",
    });
  });

  if (!columns.length) {
    return {
      targets: [],
      errors: [
        'No recognizable month columns found. Expected headers like "Jan-26_revenue".',
      ],
    };
  }

  const byKey = new Map<string, MarketTarget>();
  const errors: string[] = [];

  for (let row = 1; row < lines.length; row += 1) {
    const cells = lines[row].split("\t");
    const market = (cells[0] ?? "").trim();
    if (!market) continue;

    columns.forEach((column) => {
      const raw = cells[column.index] ?? "";
      if (raw.trim() === "") return;

      const value = parseTargetAmount(raw);

      if (value === null) {
        errors.push(
          `"${market}", ${column.month} ${column.metric}: could not read "${raw.trim()}".`,
        );
        return;
      }

      const key = `${market} ${column.month}`;
      const entry = byKey.get(key) ?? {
        market,
        month: column.month,
        revenue: null,
        profit: null,
      };
      entry[column.metric] = value;
      byKey.set(key, entry);
    });
  }

  return { targets: [...byKey.values()], errors };
}

/**
 * Sums the targets relevant to the current view: every configured market when no market
 * filter narrows it, otherwise only the selected ones, across whichever months are in
 * scope. Null (not zero) when nothing applies at all, e.g. no period is selected or none of
 * the relevant markets has a target - a comparison against "no target" would be meaningless
 * rather than a true zero.
 */
export function getCombinedTarget(
  targets: MarketTarget[],
  months: string[],
  markets: string[],
  metric: "revenue" | "profit",
): number | null {
  if (!months.length) return null;

  const marketFilter = markets.length ? new Set(markets) : null;
  let sum = 0;
  let matched = false;

  targets.forEach((target) => {
    if (!months.includes(target.month)) return;
    if (marketFilter && !marketFilter.has(target.market)) return;

    const value = target[metric];
    if (value === null) return;

    sum += value;
    matched = true;
  });

  return matched ? sum : null;
}

/**
 * Bar-fill fractions relative to a target rather than to the split's own total, so the bar
 * reads as progress toward the target instead of always looking "full". Null (meaning: fall
 * back to the ordinary 100%-of-itself split) when there is no target to measure against.
 */
export function getTargetFillPercentages(
  split: AllocationSplit,
  target: number | null,
): { reportingDate: number; budgetMonth: number } | null {
  if (!target || target <= 0) return null;

  const reportingDate = Math.abs(split.reportingDate) / target;
  const budgetMonth = Math.abs(split.budgetMonth) / target;
  const total = reportingDate + budgetMonth;

  if (total <= 1) return { reportingDate, budgetMonth };

  // Hit or exceeded the target: show a full bar, keeping the true relative proportion
  // between the two sources rather than letting one swamp the other past 100%.
  return {
    reportingDate: reportingDate / total,
    budgetMonth: budgetMonth / total,
  };
}
